import classNames from 'classnames';
import React, { useContext, useEffect, useState } from 'react';
import { AlcoholIcon } from 'src/components/shared/elements/icons/AlcoholIcon';
import { AnimeCharacterIcon } from 'src/components/shared/elements/icons/AnimeCharacterIcon';
import { BabyProductsIcon } from 'src/components/shared/elements/icons/BabyProductsIcon';
import { CosmeticsIcon } from 'src/components/shared/elements/icons/CosmeticsIcon';
import { DrugIcon } from 'src/components/shared/elements/icons/DrugIcon';
import { FoodsIcon } from 'src/components/shared/elements/icons/FoodsIcon';
import { HomeAppliancesIcon } from 'src/components/shared/elements/icons/HomeAppliancesIcon';
import { LivingTravelIcon } from 'src/components/shared/elements/icons/LivingTravelIcon';
import { ThinArrowIcon } from 'src/components/shared/elements/icons/ThinArrowIcon';
import { CategoryListForPc } from 'src/components/shared/templates/header/HamburgerMenu/CategoryListForPc';
import { CategoryListForSp } from 'src/components/shared/templates/header/HamburgerMenu/CategoryListForSp';
import { NaviList } from 'src/components/shared/templates/header/HamburgerMenu/NaviList';
import { useWindowDimensions } from 'src/hooks/useWindowDimensions';
import { I18nContext } from 'src/stores/I18n';
import { colors } from 'styles/colors';

export enum ANIMATION_CLASSES {
  SLIDE_TO_LEFT = 'animate-slideToLeft',
  SLIDE_TO_RIGHT = 'animate-slideToRight'
}

const iconMap: { [key: string]: React.FC<{ className?: string }> } = {
  alcohol: AlcoholIcon,
  anime_character: AnimeCharacterIcon,
  baby_products: BabyProductsIcon,
  cosmetics: CosmeticsIcon,
  drug: DrugIcon,
  foods: FoodsIcon,
  home_appliances: HomeAppliancesIcon,
  living_travel: LivingTravelIcon
};

export const getLargeCategoryIcon = (slug: string, className?: string): React.ReactNode => {
  const IconComponent = iconMap[slug];
  return IconComponent ? <IconComponent className={className} /> : null;
};

export const HamburgerMenu: React.FC<{ onCategoryActiveChange?: (isCategoryActive: boolean) => void }> = ({
  onCategoryActiveChange
}) => {
  const localeResources = useContext(I18nContext);
  const { width } = useWindowDimensions();
  const [breadcrumbs, setBreadcrumbs] = useState<string[]>([]);
  const [currentLargeCategory, setCurrentLargeCategory] = useState<number | null>(null);
  const [currentMiddleCategory, setCurrentMiddleCategory] = useState<number | null>(null);
  const [isPcCategoryMenuOpen, setIsPcCategoryMenuOpen] = useState<boolean>(false);
  const [animationClass, setAnimationClass] = useState<'animate-slideToLeft' | 'animate-slideToRight' | 'unset'>('unset');

  const handleSlideDirection = (direction: 'animate-slideToLeft' | 'animate-slideToRight') => {
    setAnimationClass(direction);
    setTimeout(() => {
      setAnimationClass('unset');
    }, 500);
  };

  const isCategoryActive = breadcrumbs.length >= 1 || isPcCategoryMenuOpen;

  useEffect(() => {
    if (onCategoryActiveChange) {
      onCategoryActiveChange(isCategoryActive);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCategoryActive]);

  const togglePcCategoryMenu = () => {
    setIsPcCategoryMenuOpen(!isPcCategoryMenuOpen);
  };

  const goBack = () => {
    if (width >= 576) {
      togglePcCategoryMenu();
      return;
    }

    if (currentMiddleCategory !== null) {
      setCurrentMiddleCategory(null);
      setBreadcrumbs((prev) => prev.slice(0, -1));
      handleSlideDirection(ANIMATION_CLASSES.SLIDE_TO_RIGHT);
    } else if (currentLargeCategory !== null) {
      setCurrentLargeCategory(null);
      setBreadcrumbs([localeResources.category.title]);
      handleSlideDirection(ANIMATION_CLASSES.SLIDE_TO_RIGHT);
    } else {
      setBreadcrumbs([]);
      setCurrentLargeCategory(null);
      setCurrentMiddleCategory(null);
    }
  };

  const handleLargeCategoryClick = (id: number | null) => {
    handleSlideDirection(ANIMATION_CLASSES.SLIDE_TO_LEFT);
    setCurrentLargeCategory(id);
  };

  const handleMiddleCategoryClick = (id: number | null) => {
    handleSlideDirection(ANIMATION_CLASSES.SLIDE_TO_LEFT);
    setCurrentMiddleCategory(id);
  };

  return (
    <div className={classNames('pt-7', 'semiSm:pt-2')}>
      {isCategoryActive && (
        <button onClick={goBack} className="absolute pl-5 semiSm:pl-4">
          <ThinArrowIcon className="w-5 h-5 semiSm:w-4 semiSm:h-4" direction="left" fillColor={colors.gray900} />
        </button>
      )}
      <li className={classNames('flex flex-col gap-3 text-2xl no-underline pt-12', 'semiSm:text-[15px] semiSm:pt-9')}>
        <a
          href="/kaimono"
          rel="noopener noreferrer"
          className={classNames('px-5 flex items-center justify-start text-gray900 w-full', 'semiSm:px-4')}
        >
          <span
            className={classNames(
              'text-2xl bg-gray100 w-9 h-9 rounded-full flex items-center justify-center mr-4 icon-shop',
              'semiSm:text-lg'
            )}
          ></span>
          {localeResources.product.shopping}
        </a>
        <div className="flex flex-col gap-4 semiSm:gap-0">
          <div className="semiSm:hidden">
            <CategoryListForSp
              breadcrumbs={breadcrumbs}
              setBreadcrumbs={setBreadcrumbs}
              currentLargeCategory={currentLargeCategory}
              setCurrentLargeCategory={handleLargeCategoryClick}
              currentMiddleCategory={currentMiddleCategory}
              setCurrentMiddleCategory={handleMiddleCategoryClick}
              animationClass={animationClass}
              handleSlideDirection={handleSlideDirection}
            />
          </div>
          <div className="hidden semiSm:block">
            <CategoryListForPc
              isPcCategoryMenuOpen={isPcCategoryMenuOpen}
              togglePcCategoryMenu={togglePcCategoryMenu}
            />
          </div>
          {!isCategoryActive && <NaviList />}
        </div>
      </li>
    </div>
  );
};
